import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Map, ChevronRight, FlaskConical, BookOpen, Sparkles, ShieldCheck, DollarSign, FileText, Tag, MapPin, Briefcase, Newspaper, Database, Wrench } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import JsonLd from "../components/JsonLd";
import { usePageSEO } from "../hooks/usePageSEO";
import { blogApi, fetchPeptides } from "../api";
import { STATE_LOCATIONS } from "../data/stateLocations";

const BASE = "https://www.peptidesformedspas.org";

// dbId -> slug (mirrors PeptideDetailPage)
const PEPTIDE_SLUGS = {
  "1": "ghk-cu", "3": "nad", "4": "5-amino-1mq",
  "5": "bpc-157", "6": "tb-500", "8": "sermorelin",
  "9": "mots-c", "10": "pt-141", "11": "klotho",
};

const STATIC_SECTIONS = [
  {
    id: "main",
    icon: Map,
    title: "Main",
    links: [
      { to: "/", label: "Home" },
      { to: "/shop", label: "Shop — Full Research Catalog" },
      { to: "/blog", label: "Blog — Research Articles" },
      { to: "/recovery-guide", label: "Recovery Guide" },
      { to: "/about", label: "About" },
    ],
  },
  {
    id: "categories",
    icon: Tag,
    title: "Categories (Pillar Pages)",
    links: [
      { to: "/peptides-for-med-spas", label: "Peptides for Med Spas (Hub)" },
      { to: "/anti-aging-peptides", label: "Anti-Aging & Skin Peptides" },
      { to: "/weight-loss-peptides", label: "Weight Loss & Metabolism Peptides" },
      { to: "/recovery-peptides", label: "Recovery & Repair Peptides" },
      { to: "/hormone-peptides", label: "Hormone & Longevity Peptides" },
      { to: "/sexual-wellness-peptides", label: "Sexual Wellness Peptides" },
      { to: "/topical-peptides", label: "Topical Peptides" },
      { to: "/peptide-glossary", label: "Peptide Glossary" },
    ],
  },
  {
    id: "commercial",
    icon: DollarSign,
    title: "Programs & Partners",
    links: [
      { to: "/provider-access", label: "Provider Access — Institutional Pricing" },
      { to: "/affiliate-program", label: "Become an Affiliate" },
      { to: "/partners", label: "Partners — AlphaSync Rx & MyDose.ai" },
    ],
  },
  {
    id: "business",
    icon: Briefcase,
    title: "For Business (B2B)",
    links: [
      { to: "/for-med-spas", label: "For Med Spa Owners" },
      { to: "/for-providers", label: "For Licensed Providers (NP, PA, MD, DO)" },
      { to: "/wholesale-peptides-for-clinics", label: "Wholesale Research Peptides" },
      { to: "/peptide-protocols-for-aesthetic-practices", label: "Peptide Protocols Reference" },
    ],
  },
  {
    id: "locations",
    icon: MapPin,
    title: "Locations",
    links: [
      { to: "/locations", label: "Med Spa Peptides by State (Index)" },
    ],
  },
  {
    id: "tools",
    icon: Wrench,
    title: "Tools & Press",
    links: [
      { to: "/tools/state-regulatory-checker", label: "State Regulatory Checker (interactive)" },
      { to: "/glossary", label: "Peptide Glossary — Free JSON API" },
      { to: "/press", label: "Press & Media Kit" },
      { to: "/editorial-standards", label: "Editorial Standards" },
    ],
  },
  {
    id: "policy",
    icon: ShieldCheck,
    title: "Compliance & Policies",
    links: [
      { to: "/compliance", label: "Compliance & Regulatory" },
      { to: "/disclaimer", label: "Disclaimer" },
      { to: "/privacy", label: "Privacy Policy" },
      { to: "/terms", label: "Terms of Service" },
      { to: "/refund", label: "Refund Policy" },
      { to: "/shipping", label: "Shipping Policy" },
    ],
  },
];

const SiteMapPage = () => {
  const [posts, setPosts] = useState([]);
  const [peptides, setPeptides] = useState([]);
  usePageSEO();

  useEffect(() => {
    let active = true;
    Promise.all([
      blogApi.getAll().catch((err) => {
        console.error("[SiteMapPage] blog fetch failed:", err);
        return [];
      }),
      fetchPeptides().catch((err) => {
        console.error("[SiteMapPage] peptides fetch failed:", err);
        return [];
      }),
    ]).then(([b, p]) => {
      if (!active) return;
      setPosts(b);
      setPeptides(p);
    });
    return () => { active = false; };
  }, []);

  // Sort peptides by id so the catalog list reads in seed order.
  const sortedPeptides = [...peptides].sort(
    (a, b) => parseInt(a.id, 10) - parseInt(b.id, 10)
  );

  const stateEntries = Object.values(STATE_LOCATIONS);
  const totalUrls =
    STATIC_SECTIONS.reduce((sum, s) => sum + s.links.length, 0) +
    sortedPeptides.length +
    posts.length +
    stateEntries.length;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${BASE}/sitemap#webpage`,
    url: `${BASE}/sitemap`,
    name: "Sitemap | PeptidesForMedSpas",
    description:
      "Human-readable directory of every page on PeptidesForMedSpas — pillar categories, full research catalog, blog, partner programs, and compliance policies.",
    inLanguage: "en-US",
    isPartOf: { "@id": `${BASE}/#website` },
  };

  return (
    <div className="min-h-screen bg-[#fffaf5]" data-testid="sitemap-page">
      <Header />
      <JsonLd id="sitemap-jsonld" data={jsonLd} />

      {/* Hero */}
      <section className="pt-32 pb-10 bg-gradient-to-b from-[#fdf2f4] to-[#fffaf5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center text-sm text-[#a09186] mb-6">
            <Link to="/" className="hover:text-[#b89a6d]">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-[#3a2a26] font-medium">Sitemap</span>
          </nav>
          <div className="inline-block px-4 py-2 bg-[#fdf2f4] border border-[#e8c5b8] rounded-full mb-4">
            <span className="text-sm text-[#b89a6d] font-medium">Site Directory</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3a2a26] mb-6" data-testid="sitemap-h1">
            Sitemap
          </h1>
          <p className="text-base md:text-lg text-[#5a4a40] max-w-3xl leading-relaxed">
            Every page on PeptidesForMedSpas, grouped by category. Looking for the machine-readable
            version? See{" "}
            <a
              href="/sitemap.xml"
              className="text-[#b89a6d] underline hover:text-[#a08560]"
              data-testid="sitemap-xml-link"
            >
              sitemap.xml
            </a>
            {totalUrls > 0 && <span className="text-[#a09186]"> · {totalUrls} indexed pages</span>}
          </p>
        </div>
      </section>

      {/* Static sections */}
      <section className="py-10 bg-[#fffaf5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
          {STATIC_SECTIONS.map((s) => (
            <article
              key={s.id}
              className="bg-white border border-[#f0d8d4] rounded-2xl p-7"
              data-testid={`sitemap-section-${s.id}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#fdf2f4] rounded-lg flex items-center justify-center">
                  <s.icon className="w-5 h-5 text-[#b89a6d]" />
                </div>
                <h2 className="text-base md:text-lg font-bold text-[#3a2a26]">{s.title}</h2>
              </div>
              <ul className="space-y-2">
                {s.links.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      className="text-sm text-[#5a4a40] hover:text-[#b89a6d] transition-colors flex items-center gap-1.5"
                    >
                      <ChevronRight className="w-3.5 h-3.5 text-[#b89a6d] flex-shrink-0" />
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {/* Product catalog */}
      <section className="py-10 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="bg-[#fffaf5] border border-[#f0d8d4] rounded-2xl p-7" data-testid="sitemap-section-products">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#fdf2f4] rounded-lg flex items-center justify-center">
                <FlaskConical className="w-5 h-5 text-[#b89a6d]" />
              </div>
              <h2 className="text-base md:text-lg font-bold text-[#3a2a26]">
                Research Catalog · {sortedPeptides.length} products
              </h2>
            </div>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
              {sortedPeptides.map((p) => {
                const slug = PEPTIDE_SLUGS[p.id];
                if (!slug) return null;
                return (
                  <li key={p.id}>
                    <Link
                      to={`/peptides/${slug}`}
                      className="text-sm text-[#5a4a40] hover:text-[#b89a6d] transition-colors flex items-center gap-1.5"
                      data-testid={`sitemap-product-${slug}`}
                    >
                      <ChevronRight className="w-3.5 h-3.5 text-[#b89a6d] flex-shrink-0" />
                      <span className="truncate">{p.name}</span>
                      <span className="text-xs text-[#a09186] ml-auto whitespace-nowrap">{p.category}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </article>
        </div>
      </section>

      {/* Blog posts */}
      <section className="py-10 bg-[#fffaf5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="bg-white border border-[#f0d8d4] rounded-2xl p-7" data-testid="sitemap-section-blog">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#fdf2f4] rounded-lg flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-[#b89a6d]" />
              </div>
              <h2 className="text-base md:text-lg font-bold text-[#3a2a26]">
                Research Blog · {posts.length} articles
              </h2>
            </div>
            {posts.length === 0 ? (
              <p className="text-sm text-[#a09186]">Loading articles…</p>
            ) : (
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                {posts.map((post) => (
                  <li key={post.slug}>
                    <Link
                      to={`/blog/${post.slug}`}
                      className="text-sm text-[#5a4a40] hover:text-[#b89a6d] transition-colors flex items-start gap-1.5"
                      data-testid={`sitemap-blog-${post.slug}`}
                    >
                      <ChevronRight className="w-3.5 h-3.5 text-[#b89a6d] flex-shrink-0 mt-1" />
                      <span className="line-clamp-2">{post.title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </article>
        </div>
      </section>

      {/* State locations */}
      <section className="py-10 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="bg-[#fffaf5] border border-[#f0d8d4] rounded-2xl p-7" data-testid="sitemap-section-states">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#fdf2f4] rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[#b89a6d]" />
              </div>
              <h2 className="text-base md:text-lg font-bold text-[#3a2a26]">
                State Location Pages · {stateEntries.length} states
              </h2>
            </div>
            <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-6 gap-y-2">
              {stateEntries.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/locations/${s.slug}`}
                    className="text-sm text-[#5a4a40] hover:text-[#b89a6d] transition-colors flex items-center gap-1.5"
                    data-testid={`sitemap-state-${s.slug}`}
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-[#b89a6d] flex-shrink-0" />
                    <span className="truncate">{s.name}</span>
                    <span className="text-xs text-[#a09186] ml-auto">{s.abbrev}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </article>
        </div>
      </section>

      {/* Footer note */}
      <section className="py-8 bg-[#fffaf5]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-[#7a6b62]">
            All product listings are research chemicals supplied for laboratory research only.
            Not for human or animal use. See our{" "}
            <Link to="/compliance" className="text-[#b89a6d] underline hover:text-[#a08560]">Compliance page</Link>
            {" "}for our full regulatory posture.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SiteMapPage;
